/**
 * Credit balance + get-more-credits UI (#44, #125; FR-12.1/12.3/12.5/12.6, FR-9.5).
 *
 * Credits are the billing currency — 1 credit = 1 paid-path game (dynamic generation
 * or auto-host voice); only the host spends, players always join free. Following
 * FR-9.5 (one goal = one action) there is a SINGLE "get more credits" slot: a registered
 * host can **buy a pack** (Stripe Checkout, #125) and/or **redeem a code**; a guest sees a
 * sign-in steer for purchase (paid credits bind to a registered account, D-125.5) and can
 * still redeem. Not two competing goals — one slot, the two ways to add credits.
 *
 * Four pieces, kept in one file (like GenerationStatus + its banner):
 *  - {@link CreditBalance}: the balance display + the get-more-credits slot.
 *  - {@link CreditPacks}: the pack picker (buy credits) — registered accounts only (#125).
 *  - {@link RedeemCodeForm}: the minimal reusable redeem form (input + submit).
 *  - {@link InsufficientCreditsNotice}: the host-lobby recovery shown when the server
 *    denies a paid-path start with `INSUFFICIENT_CREDITS` — one message, one action.
 */

import { useEffect, useState } from 'react';
import {
  fetchCreditBalance,
  redeemCode,
  type CreditBalance as CreditBalanceValue,
} from '../net/credits-api';
import { fetchCreditPacks, startCheckout, type CreditPack } from '../net/billing-api';
import { BRAND_NAME } from './brand';

/** Cost/scope reminder shared by the balance display and the insufficient notice. */
const CREDIT_HINT =
  'Generating a game or auto-hosting one costs 1 credit. Joining a game is always free.';

interface CreditPacksProps {
  /** Signed guest token proving the caller's identity (FR-9.3). */
  guestToken: string;
}

/**
 * The pack picker — the purchase half of the single "get more credits" slot (#125, R-8,
 * FR-9.5). Registered hosts only (a guest sees a sign-in steer instead; the server also
 * enforces registered-only, D-125.5). Renders one buy button per catalog pack; clicking
 * one creates a Stripe Checkout Session and redirects to the hosted page. Renders nothing
 * when the catalog is empty (payments unavailable) so the redeem affordance still stands
 * alone. The product name comes from {@link BRAND_NAME}, never a baked literal (D-125.7).
 */
export function CreditPacks({ guestToken }: CreditPacksProps): React.JSX.Element | null {
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchCreditPacks().then((p) => {
      if (!cancelled) setPacks(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (packs.length === 0) return null;

  const buy = async (sku: string): Promise<void> => {
    setBusy(sku);
    setError(null);
    try {
      // On success this redirects to Stripe and never returns.
      await startCheckout(guestToken, sku);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout.');
      setBusy(null);
    }
  };

  return (
    <div className="credit-packs" data-testid="credit-packs">
      <p className="muted">Buy more credits for {BRAND_NAME}:</p>
      <div className="controls" data-testid="credit-pack-buttons">
        {packs.map((pack) => (
          <button
            key={pack.sku}
            type="button"
            className="btn btn--primary"
            data-testid={`credit-pack-${pack.sku}`}
            disabled={busy !== null}
            onClick={() => void buy(pack.sku)}
          >
            {busy === pack.sku
              ? 'Starting…'
              : `${pack.credits} credits — $${pack.priceUsd.toFixed(2)}`}
          </button>
        ))}
      </div>
      {error && (
        <div className="error-text" data-testid="credit-pack-error">
          {error}
        </div>
      )}
    </div>
  );
}

interface RedeemCodeFormProps {
  /** Signed guest token proving the caller's identity (FR-9.3). */
  guestToken: string;
  /** Called with the new balance after a successful redemption. */
  onRedeemed: (balance: CreditBalanceValue) => void;
  /** Optional dismiss affordance (a Cancel button); omitted → no Cancel. */
  onCancel?: () => void;
}

/**
 * The minimal redeem form: a code input + submit. On success it hands the updated
 * balance back to the parent (which updates what it shows and dismisses); on a
 * rejected code it shows the server's friendly per-status message inline.
 */
export function RedeemCodeForm({
  guestToken,
  onRedeemed,
  onCancel,
}: RedeemCodeFormProps): React.JSX.Element {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const trimmed = code.trim();
    if (trimmed === '') {
      setError('Enter a code to redeem.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { balance } = await redeemCode(guestToken, trimmed);
      onRedeemed(balance);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not redeem that code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="redeem-form"
      data-testid="redeem-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <input
        type="text"
        aria-label="Redeem code"
        data-testid="redeem-code-input"
        placeholder="Enter a code"
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
          setError(null);
        }}
        autoFocus
      />
      <div className="controls">
        <button
          type="submit"
          className="btn btn--primary"
          data-testid="redeem-submit"
          disabled={busy}
        >
          {busy ? 'Redeeming…' : 'Redeem'}
        </button>
        {onCancel && (
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="redeem-cancel"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
      </div>
      {error && (
        <div className="error-text" data-testid="redeem-error">
          {error}
        </div>
      )}
    </form>
  );
}

interface CreditBalanceProps {
  /** Signed guest token proving the caller's identity (FR-9.3). */
  guestToken: string;
  /**
   * Whether the caller is a registered account (#125, D-125.5). Registered → the pack
   * picker (buy credits) renders in the get-more-credits slot; a guest sees a sign-in
   * steer instead, and no purchase button (paid credits bind to a registered account).
   * Defaults to `false` (guest) so an omitting caller never shows purchase.
   */
  registered?: boolean;
  /**
   * Reports the balance up to the create screen whenever it loads or changes after a
   * redemption (#171 review, PR #179), so the parent can hide the "Create Game" button
   * when the host is out of credits — the fetch already lives here, so the parent reuses
   * it rather than reading the balance twice.
   */
  onBalanceChange?: (balance: CreditBalanceValue) => void;
  /**
   * On a successful checkout return (#197, FR-12.6.1) the number of credits just granted —
   * the AUTHORITATIVE amount the server stamped on the return URL, captured by the page
   * before the params self-clear. When present (> 0) the balance ties itself to the
   * purchase: a "+N" delta chip beside the total with a one-time pulse (reduced-motion
   * aware). Display-only — the durable grant is the webhook's job; this never refetches or
   * mutates. Omitted on a normal visit → no highlight.
   */
  successDelta?: number;
}

/**
 * Show the host their credit balance (the single `total`), with the single get-more-credits
 * slot (FR-9.5): a "Redeem a code" affordance for everyone plus — for a registered account —
 * the pack picker to buy more credits (#125, R-8). A guest sees a sign-in steer for purchase
 * (paid credits bind to a registered account, D-125.5). Fetches on mount; a fetch failure
 * degrades to a quiet message rather than blocking the surface.
 */
export function CreditBalance({
  guestToken,
  registered = false,
  onBalanceChange,
  successDelta,
}: CreditBalanceProps): React.JSX.Element {
  // A positive granted amount → highlight the total as just-purchased (#197). Guard the
  // sign so a stray `0`/negative never renders a chip.
  const showDelta = successDelta !== undefined && successDelta > 0;
  const [balance, setBalance] = useState<CreditBalanceValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchCreditBalance(guestToken)
      .then((b) => {
        if (cancelled) return;
        setBalance(b);
        onBalanceChange?.(b);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your credit balance.');
      });
    return () => {
      cancelled = true;
    };
  }, [guestToken, onBalanceChange]);

  return (
    <section className="credit-balance" data-testid="credit-balance">
      <h2 className="app-title">Credits</h2>
      {balance ? (
        <p
          className={`credit-balance__total${showDelta ? ' credit-balance__total--purchased' : ''}`}
          data-testid="credit-balance-total"
        >
          <strong>{balance.total}</strong> {balance.total === 1 ? 'credit' : 'credits'}
          {showDelta && (
            <span className="credit-balance__delta" data-testid="credit-balance-delta">
              +{successDelta}
            </span>
          )}
        </p>
      ) : error ? (
        <p className="muted" data-testid="credit-balance-error">
          {error}
        </p>
      ) : (
        <p className="muted" data-testid="credit-balance-loading">
          Loading your balance…
        </p>
      )}
      <p className="muted">{CREDIT_HINT}</p>
      {/* The single get-more-credits slot (FR-9.5): buy a pack (registered only, #125) and/or
          redeem a code. A guest gets a sign-in steer for purchase — no buy button. */}
      {registered ? (
        <CreditPacks guestToken={guestToken} />
      ) : (
        <p className="muted" data-testid="credit-purchase-signin">
          Sign in to buy more credits.
        </p>
      )}
      {redeeming ? (
        <RedeemCodeForm
          guestToken={guestToken}
          onRedeemed={(b) => {
            setBalance(b);
            onBalanceChange?.(b);
            setError(null);
            setRedeeming(false);
          }}
          onCancel={() => setRedeeming(false)}
        />
      ) : (
        <button
          type="button"
          className="btn btn--ghost"
          data-testid="credit-redeem-open"
          onClick={() => setRedeeming(true)}
        >
          Redeem a code
        </button>
      )}
    </section>
  );
}

interface InsufficientCreditsNoticeProps {
  /** Signed guest token proving the caller's identity (FR-9.3). */
  guestToken: string;
  /** Optional hook so the host surface can refresh a shown balance after top-up. */
  onRedeemed?: (balance: CreditBalanceValue) => void;
}

/**
 * Host-lobby recovery when the server denies a paid-path start with
 * `INSUFFICIENT_CREDITS` (FR-12.3). The game stays in the lobby; this shows a clear
 * message and a SINGLE recovery action — redeem a code (FR-9.5). There is no second
 * way to start the game and no purchase button. After a successful redemption it
 * confirms and lets the host re-use the existing start control.
 */
export function InsufficientCreditsNotice({
  guestToken,
  onRedeemed,
}: InsufficientCreditsNoticeProps): React.JSX.Element {
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  return (
    <div className="error-text insufficient-credits" data-testid="insufficient-credits">
      <p className="insufficient-credits__reason">You’re out of credits. {CREDIT_HINT}</p>
      {redeemed ? (
        <p className="muted" data-testid="insufficient-credits-redeemed">
          Credits added — you can start your game now.
        </p>
      ) : redeeming ? (
        <RedeemCodeForm
          guestToken={guestToken}
          onRedeemed={(b) => {
            setRedeeming(false);
            setRedeemed(true);
            onRedeemed?.(b);
          }}
          onCancel={() => setRedeeming(false)}
        />
      ) : (
        <button
          type="button"
          className="btn btn--primary"
          data-testid="insufficient-credits-redeem"
          onClick={() => setRedeeming(true)}
        >
          Redeem a code
        </button>
      )}
    </div>
  );
}
